/* eslint-disable no-promise-executor-return */
/* eslint-disable no-redeclare */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable default-param-last */
import {
  attachUnitLeaseRelations,
  ChangeProfessionalActivityForLeaseInput,
  ChangeProfessionalActivityForLeaseMutation,
  ChangeProfessionalActivityForLeaseMutationVariables,
  CommunicationSettingsProfile,
  Contact,
  CreateLeaseInput as CreateInput,
  CreateLeaseAdditionalClauseInput,
  CreateLeaseAdditionalClauseMutationVariables,
  CreateLeaseMutationVariables as CreateMutationVariables,
  CustomSubscription,
  CustomSubscriptionType,
  DEFAULT_GRAPHQL_OBJECT_DEPTH_FOR_SYNC_QUERY,
  DeepDeletableEntityType,
  DeleteEntityByIdMutationVariables,
  DeleteLeaseMutationVariables as DeleteMutationVariables,
  File as FileModel,
  FolderEntity,
  LEASE_TABLE_NAME,
  Lease,
  LeaseAdditionalClause,
  Model,
  MutationStatus,
  OnCreateCustomSubscriptionSubscription,
  OnCreateLeaseSubscription,
  OnDeleteCustomSubscriptionSubscription,
  OnDeleteLeaseSubscription,
  OnUpdateCustomSubscriptionSubscription,
  OnUpdateLeaseSubscription,
  UnitLease,
  UpdateLeaseAdditionalClauseInput,
  changeProfessionalActivityForLease as changeProfessionalActivityForLeaseMutation,
  cleanInputCreate,
  cleanInputUpdate,
  createLeaseAdditionalClause as createLeaseAdditionalClauseMutation,
  createLease as createMutation,
  deleteEntityById as deleteEntityByIdMutation,
  deleteLeaseAdditionalClause as deleteLeaseAdditionalClauseMutation,
  deleteLease as deleteMutation,
  getLeaseAdditionalClause as getLeaseAdditionalClauseQuery,
  getLease as getQuery,
  getReadId,
  getTableClientId,
  getTypedCustomSubscriptionValueForRefetch,
  isNilOrEmpty,
  onCreateCustomSubscription,
  onCreateLease,
  onDeleteCustomSubscription,
  onDeleteLease,
  onUpdateCustomSubscription,
  onUpdateLease,
  removeNestedFieldsExcept,
  resolveOneToMany,
  syncLeaseAdditionalClauses,
  syncLeases,
  todayDateTime,
  uniqueObjectWithIdAppend,
  updateLeaseAdditionalClause as updateLeaseAdditionalClauseMutation,
  updateLease as updateMutation,
  createMandateLink as createMandateLinkMutation,
  type CreateMandateLinkMutation,
  type CreateMandateLinkMutationVariables,
} from '@rentguru/commons-utils';
import {
  deleteAndHideEntityWithFetchBefore,
  deleteEntityWithFetchBefore,
  get,
  getFilterFieldNameForIndex,
  list,
  mutation,
  mutationWithFetchBefore,
  recordWasUpdated,
  useSubscriptions,
} from '@up2rent/graphql';
import isNil from 'lodash/isNil';
import React, { useContext, useEffect, useMemo } from 'react';

import { useBuildings } from './BuildingsContext';
import { useContacts } from './ContactsContext';
import { ContextLoaderAction, ContextLoaderStore, useContextLoader } from './ContextLoader';
import { getFolderDepthForNavigation, type FolderDatabaseModel } from './Folderhook/folderContextUtils';
import { useFolderDatabase } from './Folderhook/FolderDatabaseContext';
import { LeaseContactContext, useLeaseContacts } from './LeaseContactContext';
import { useLeaseVariousOperations } from './LeaseVariousOperationsContext';
import { UnitLeaseContext, useUnitLeases } from './UnitLeaseContext';
import { useUnits } from './UnitsContext';
import { useUser } from './UserContext';
import { splitLeaseContactsByRole } from './utils/leaseContactUtils';
import { usePermissions } from './utils/PermissionsContext';

const ENTITY_MODEL_NAME: Model = 'Lease';

export type ActionLease =
  | {
      type: 'SHOULD_FETCH_LEASE' | 'IS_FETCHING_LEASE';
    }
  | {
      type: 'ADD_LEASE';
      payload: { lease: Lease };
    }
  | {
      type: 'FETCHED_LEASE';
      payload: { leases: Lease[] };
    }
  | {
      type: 'UPDATE_LEASE';
      payload: { lease: Lease };
    }
  | {
      type: 'DELETE_LEASE' | 'FETCH_LEASE';
      payload: { id: string };
    };

export interface LeaseContext {
  leases: LeaseExtended[];
  getLeaseFilesAndLease: (leaseId: string) => Promise<{ files: FileModel[]; lease: Lease }>;
  getLease: (id: string) => LeaseExtended | null | undefined;
  getLeaseFromUnitLease: (unitLeaseId: string) => LeaseExtended | null | undefined;
  createLease: (input: Omit<Lease, 'clientId' | 'readId'>) => Promise<Lease>;
  updateLease: (original: Lease, updates: Partial<Lease>) => Promise<Lease>;
  deleteLease: (id: string) => Promise<Lease | null>;
  createLeaseContact: LeaseContactContext['createLeaseContact'];
  updateLeaseContact: LeaseContactContext['updateLeaseContact'];
  deleteLeaseContact: LeaseContactContext['deleteLeaseContact'];
  createAdditionalClause: (
    input: Omit<LeaseAdditionalClause, 'id' | 'clientId' | 'readId'>
  ) => Promise<LeaseAdditionalClause>;
  getLeaseAdditionalClauses: (leaseId: string) => Promise<LeaseAdditionalClause[]>;
  updateAdditionalClause: (updates: UpdateLeaseAdditionalClauseInput) => Promise<LeaseAdditionalClause>;
  deleteAdditionalClause: (additionalClause: LeaseAdditionalClause) => Promise<LeaseAdditionalClause | null>;
  createUnitLease: UnitLeaseContext['createUnitLease'];
  updateUnitLease: UnitLeaseContext['updateUnitLease'];
  deleteUnitLease: UnitLeaseContext['deleteUnitLease'];
  getAllContactsOwnersFromLease: (leaseId: string) => Contact[];
  getUnitsOfLease: (leaseId: string) => UnitLease[];
  createMandateLink: (leaseId: string) => Promise<void>;
  deepDeleteLease: (id: string) => Promise<MutationStatus | undefined>;
  changeProfessionalActivityForLease: (
    input: Omit<ChangeProfessionalActivityForLeaseInput, 'clientId' | 'userId'>
  ) => Promise<ChangeProfessionalActivityForLeaseMutation>;
  leasesLoading: boolean | undefined;
  leasesError: string | undefined;
  setFetchLease: () => void;
}

export interface LeaseExtended extends Lease {
  tenants: Contact[];
  guarantors: Contact[];
  lessors: Contact[];
}
export type LeaseExtendedForTable = LeaseExtended & { owner: Contact } & {
  communicationSettingsProfile: CommunicationSettingsProfile;
};

export const leaseReducerDelegation = (state: ContextLoaderStore, action: ContextLoaderAction): ContextLoaderStore => {
  switch (action.type) {
    case 'SHOULD_FETCH_LEASE':
      if (state.Lease.loading || state.Lease.shouldFetch) {
        return state;
      }
      return {
        ...state,
        Lease: { ...state.Lease, shouldFetch: true },
      };
    case 'IS_FETCHING_LEASE':
      if (state.Lease.loading) {
        return state;
      }
      return {
        ...state,
        Lease: { ...state.Lease, loading: true },
      };
    case 'FETCHED_LEASE':
      return {
        ...state,
        Lease: {
          ...state.Lease,
          data: action.payload.leases,
          loading: false,
          shouldFetch: false,
          lastFetch: todayDateTime(),
        },
      };
    case 'ADD_LEASE':
      // Check if already present - If already added by this user or coming from another user
      if (state.Lease.data?.find((object) => object.id === action.payload.lease.id)) {
        return state;
      }

      return {
        ...state,
        Lease: {
          ...state.Lease,
          data: [...state.Lease.data, action.payload.lease],
        },
      };
    case 'UPDATE_LEASE':
      // No data
      if (isNilOrEmpty(state.Lease.data)) {
        return state;
      }

      // Already present and same object
      const currentObject = state.Lease.data?.find((object) => object.id === action.payload.lease.id);
      if (!currentObject) {
        return {
          ...state,
          Lease: {
            ...state.Lease,
            data: [...state.Lease.data, action.payload.lease],
          },
        };
      }
      if (!recordWasUpdated(currentObject, action.payload.lease)) {
        return state;
      }

      // Update
      return {
        ...state,
        Lease: {
          ...state.Lease,
          data: state.Lease.data.map((object) => {
            if (object.id === action.payload.lease.id) {
              return action.payload.lease;
            }
            return object;
          }),
        },
      };
    case 'DELETE_LEASE':
      if (isNilOrEmpty(state.Lease.data)) {
        return state;
      }
      return {
        ...state,
        Lease: {
          ...state.Lease,
          data: state.Lease.data.filter((object) => object.id !== action.payload.id),
        },
      };

    default:
      return state;
  }
};

const LEASE_RELATIONS_TO_REMOVE = [
  'additionalClauses',
  'bankAccount',
  'contacts',
  'files',
  'invoiceSender',
  'leaseActionHistories',
  'leaseInventories',
  'leasePriceHistories',
  'signatureDocuments',
  'units',
  'variousOperations',
  'vatRateRent',
];
const LEASE_NESTED_FIELDS_TO_KEEP = ['rentalGuaranty', 'customValues', 'advancedChargesInvoices', 'paymentOption'];

const LEASES_QUERY = removeNestedFieldsExcept(
  syncLeases,
  DEFAULT_GRAPHQL_OBJECT_DEPTH_FOR_SYNC_QUERY,
  LEASE_RELATIONS_TO_REMOVE,
  LEASE_NESTED_FIELDS_TO_KEEP
);

const fetchLeases = async (clientId: string, additionalFilter?: object): Promise<Lease[]> =>
  list<Lease>(
    LEASES_QUERY,
    getFilterFieldNameForIndex('byClientId'),
    getTableClientId(clientId, ENTITY_MODEL_NAME),
    additionalFilter
  );

export const fetchLease = async (id: string): Promise<Lease> => await get<Lease>(getQuery, id);

export const LeaseContext = React.createContext<LeaseContext | null>(null);

export const LeaseContextProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const {
    Lease: { data: leasesLoader, loading: leasesLoading, shouldFetch },
    UnitLease: { data: unitLeases, loading: unitLeasesLoading },
    BankAccount: { data: bankAccounts, loading: bankAccountsLoading },
    dispatch: contextDispatch,
  } = useContextLoader();
  const {
    createLeaseContact,
    updateLeaseContact,
    deleteLeaseContact,
    leaseContacts: leaseContactsFromContext,
    loading: leaseContactsLoading,
  } = useLeaseContacts();
  const { contacts: bookContacts, ocrDrafts, getContact, contactsLoading } = useContacts();
  const { createUnitLease, updateUnitLease, deleteUnitLease } = useUnitLeases();
  const { leasesCreationDelete } = usePermissions();
  const { units, unitsLoading } = useUnits();
  const { buildings } = useBuildings();
  const { clientId, userId } = useUser();
  const { leaseVariousOperations, loading: lvoLoading } = useLeaseVariousOperations();
  const {
    getFoldersWithCustomFilter,
    updateDatabaseFolder,
    getFoldersByDepthRange,
    bulkUpdateDatabaseFolders,
    bulkDeleteFolders,
  } = useFolderDatabase();

  const loading =
    leasesLoading ||
    leaseContactsLoading ||
    unitsLoading ||
    unitLeasesLoading ||
    bankAccountsLoading ||
    contactsLoading ||
    lvoLoading;

  const setFetchLease = () => {
    contextDispatch({ type: 'SHOULD_FETCH_LEASE' });
  };

  const getLeaseFilesAndLease = async (leaseId: string): Promise<{ files: FileModel[]; lease: Lease }> => {
    const leaseResult = await fetchLease(leaseId);

    return {
      files: leaseResult.files ?? [],
      lease: leaseResult,
    };
  };

  const leases = useMemo<LeaseExtended[]>(() => {
    if (loading) {
      return [];
    }
    const leasesById = new Map(leasesLoader.map((loadedLease) => [loadedLease.id, loadedLease]));
    const unitsById = new Map(units.map((unit) => [unit.id, unit]));
    const linkedUnitLeases = attachUnitLeaseRelations(unitLeases, leasesById, unitsById);
    const contactsById = new Map<string, Contact>(
      [...bookContacts, ...ocrDrafts].map((contact) => [contact.id, contact])
    );
    const leasesReduced = leasesLoader.reduce((acc: LeaseExtended[], lease: Lease) => {
      const {
        contacts: leaseContacts,
        tenants,
        guarantors,
        lessors,
      } = splitLeaseContactsByRole(resolveOneToMany(lease.id, leaseContactsFromContext, 'leaseId'), contactsById);
      const extendedLease = {
        ...lease,
        advancedChargesInvoices: lease.advancedChargesInvoices
          ? {
              ...lease.advancedChargesInvoices,
              __typename: undefined,
            }
          : undefined,
        invoiceSender: getContact(lease.invoiceSenderId),
        bankAccount: bankAccounts.find((b) => b.id === lease.bankAccountId),
        units: resolveOneToMany(lease.id, linkedUnitLeases, 'leaseId'),
        variousOperations: (leaseVariousOperations ?? []).filter((lvo) => lvo.leaseId === lease.id),
        contacts: leaseContacts,
        tenants,
        guarantors,
        lessors,
      };
      if (isNilOrEmpty(extendedLease.units)) {
        return acc;
      }
      acc.push(extendedLease);
      return acc;
    }, []);
    return leasesReduced;
  }, [
    loading,
    leasesLoader,
    leaseContactsFromContext,
    bookContacts,
    ocrDrafts,
    getContact,
    bankAccounts,
    unitLeases,
    units,
    leaseVariousOperations,
  ]);

  useEffect(() => {
    const fetchAndSet = async () => {
      contextDispatch({ type: 'IS_FETCHING_LEASE' });
      try {
        const result = await fetchLeases(clientId!);
        contextDispatch({ type: 'FETCHED_LEASE', payload: { leases: result } });
      } catch (error) {
        console.error('[leases] fetching leases failed', error);
        contextDispatch({ type: 'FETCH_FAILED', payload: { model: 'Lease' } });
      }
    };
    if (shouldFetch) fetchAndSet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetch]);

  useSubscriptions<OnCreateLeaseSubscription, OnUpdateLeaseSubscription, OnDeleteLeaseSubscription>(
    onCreateLease,
    onUpdateLease,
    onDeleteLease,
    (data) => {
      contextDispatch({
        type: 'ADD_LEASE',
        payload: { lease: data.onCreateLease as Lease },
      });
    },
    async (data) => {
      contextDispatch({
        type: 'UPDATE_LEASE',
        payload: { lease: data.onUpdateLease as Lease },
      });
      const leaseFolder = await getFoldersWithCustomFilter(
        (folder) => folder.entityId === data.onUpdateLease?.id && folder.folderEntity === FolderEntity.LEASE
      ).then((folders) => folders[0]);
      if (!isNilOrEmpty(leaseFolder) && leaseFolder?.translatedLabel !== data.onUpdateLease?.name) {
        const updatedFolder: FolderDatabaseModel = {
          ...leaseFolder,
          translatedLabel: data.onUpdateLease?.name,
        };
        await updateDatabaseFolder(updatedFolder);

        const foldersInLease = await getFoldersByDepthRange(getFolderDepthForNavigation(leaseFolder));
        const foldersToUpdate = foldersInLease.map((folder) => ({
          key: folder.id,
          changes: {
            depthString: folder.depthString.map((depth) =>
              depth === leaseFolder.translatedLabel ? data.onUpdateLease!.name : depth
            ),
          },
        }));
        await bulkUpdateDatabaseFolders(foldersToUpdate);
      }
    },
    async (data) => {
      const { id } = data.onDeleteLease as Lease;
      contextDispatch({
        type: 'DELETE_LEASE',
        payload: { id },
      });

      const leaseFolders = await getFoldersWithCustomFilter(
        (folder) => folder.entityId === data.onDeleteLease?.id && folder.folderEntity === FolderEntity.LEASE
      );
      const leaseFolder = leaseFolders[0];
      if (!leaseFolder) {
        return;
      }
      const foldersInLease = await getFoldersByDepthRange(getFolderDepthForNavigation(leaseFolder));
      const foldersToDelete = [leaseFolder, ...foldersInLease].map((folder) => folder.id);
      if (!isNilOrEmpty(foldersToDelete)) {
        await bulkDeleteFolders(foldersToDelete);
      }
    }
  );

  useSubscriptions<
    OnCreateCustomSubscriptionSubscription,
    OnUpdateCustomSubscriptionSubscription,
    OnDeleteCustomSubscriptionSubscription
  >(
    onCreateCustomSubscription,
    onUpdateCustomSubscription,
    onDeleteCustomSubscription,
    (data) => {
      const newData = data.onCreateCustomSubscription as CustomSubscription;
      if (
        newData.name === CustomSubscriptionType.REFETCH &&
        getTypedCustomSubscriptionValueForRefetch(newData).table === LEASE_TABLE_NAME
      ) {
        setFetchLease();
      }
    },
    () => {},
    () => {}
  );

  const getLease = (id: string) => {
    if (leasesLoading) {
      return null;
    }
    return leases.find((lease) => lease.id === id);
  };

  const getLeaseFromUnitLease = (unitLeaseId: string) => {
    if (leasesLoading) {
      return null;
    }
    return leases.find((lease) => lease.units?.some((unitLease) => unitLease.id === unitLeaseId));
  };

  const createLease = async (input: Omit<Lease, 'clientId' | 'readId'>) => {
    const lease = await mutation<Lease, CreateMutationVariables>(createMutation, {
      input: {
        ...(cleanInputCreate(input) as CreateInput),
        clientId: getTableClientId(clientId!, ENTITY_MODEL_NAME),
        readId: getReadId(clientId!, ENTITY_MODEL_NAME),
      },
    });
    contextDispatch({ type: 'ADD_LEASE', payload: { lease } });
    return lease;
  };

  const updateLease = async (original: Lease, updates: Partial<Lease>) => {
    const result = await mutationWithFetchBefore<Lease>(original.id, getQuery, updateMutation, {
      input: { ...cleanInputUpdate({ id: original.id, _version: original._version, ...updates }, false) },
    });
    contextDispatch({ type: 'UPDATE_LEASE', payload: { lease: result } });
    return result;
  };

  const deleteLease = async (id: string) => {
    const lease = getLease(id)!;
    if (!leasesCreationDelete) {
      return null;
    }
    await deleteEntityWithFetchBefore<Pick<Lease, 'id'>, DeleteMutationVariables>({ id }, getQuery, deleteMutation);
    contextDispatch({ type: 'DELETE_LEASE', payload: { id } });
    return lease;
  };

  const createAdditionalClause = async (
    input: Omit<LeaseAdditionalClause, 'id' | 'clientId' | 'readId'>
  ): Promise<LeaseAdditionalClause> => {
    const leaseAdditionalClause = await mutation<LeaseAdditionalClause, CreateLeaseAdditionalClauseMutationVariables>(
      createLeaseAdditionalClauseMutation,
      {
        input: {
          ...(cleanInputCreate(input) as CreateLeaseAdditionalClauseInput),
          readId: getReadId(clientId!, 'LeaseAdditionalClause'),
          clientId: getTableClientId(clientId!, 'LeaseAdditionalClause'),
        },
      }
    );
    return leaseAdditionalClause;
  };

  const updateAdditionalClause = async (updates: UpdateLeaseAdditionalClauseInput): Promise<LeaseAdditionalClause> => {
    const result = await mutation<LeaseAdditionalClause>(updateLeaseAdditionalClauseMutation, {
      input: { ...cleanInputUpdate(updates) },
    });
    return result;
  };

  const deleteAdditionalClause = async (
    additionalClause: LeaseAdditionalClause
  ): Promise<LeaseAdditionalClause | null> => {
    if (!leasesCreationDelete) {
      return null;
    }
    const result = await deleteAndHideEntityWithFetchBefore<LeaseAdditionalClause>(
      additionalClause,
      getLeaseAdditionalClauseQuery,
      deleteLeaseAdditionalClauseMutation
    );
    return result;
  };

  const getLeaseAdditionalClauses = async (leaseId: string): Promise<LeaseAdditionalClause[]> => {
    return await list<LeaseAdditionalClause>(syncLeaseAdditionalClauses, 'leaseId', leaseId);
  };

  const getUnitsOfLease = (leaseId: string) => {
    const currentLease = leases.find((lease) => lease.id === leaseId);
    return currentLease?.units ?? [];
  };

  const getAllContactsOwnersFromLease = (leaseId: string): Contact[] => {
    const lease = leases.find((l) => l.id === leaseId);
    if (isNil(lease)) return [];
    return (lease.units ?? []).reduce((ownerContacts: Contact[], leaseUnit: UnitLease) => {
      const completeUnit = units.find((unit) => unit.id === leaseUnit.unit?.id);
      if (isNil(completeUnit)) return ownerContacts;

      const unitOwners = (completeUnit.owners ?? []).flatMap((unitOwner) => {
        const contact = getContact(unitOwner.ownerId);
        return contact ? [contact] : [];
      });

      const completeBuilding = buildings.find((building) => building.id === completeUnit.building?.id);
      const buildingOwners = (completeBuilding?.owners ?? []).flatMap((buildingOwner) => {
        const contact = getContact(buildingOwner.contactId);
        return contact ? [contact] : [];
      });

      return uniqueObjectWithIdAppend(uniqueObjectWithIdAppend(ownerContacts, unitOwners), buildingOwners);
    }, []);
  };

  const deepDeleteLease = async (id: string) => {
    if (!leasesCreationDelete) {
      return;
    }
    return await mutation<MutationStatus, DeleteEntityByIdMutationVariables>(deleteEntityByIdMutation, {
      input: { entityId: id, entity: DeepDeletableEntityType.LEASE },
    });
  };

  const changeProfessionalActivityForLease = async (
    input: Omit<ChangeProfessionalActivityForLeaseInput, 'clientId' | 'userId'>
  ): Promise<ChangeProfessionalActivityForLeaseMutation> => {
    const response = await mutation<
      ChangeProfessionalActivityForLeaseMutation,
      ChangeProfessionalActivityForLeaseMutationVariables
    >(changeProfessionalActivityForLeaseMutation, {
      input: {
        ...(cleanInputCreate(input) as ChangeProfessionalActivityForLeaseInput),
        clientId: clientId!,
        userId: userId!,
      },
    });
    return response;
  };

  const createMandateLink = async (leaseId: string): Promise<void> => {
    await mutation<CreateMandateLinkMutation, CreateMandateLinkMutationVariables>(createMandateLinkMutation, {
      input: { clientId: clientId!, leaseId },
    });
  };

  const values = useMemo(
    () => ({
      leases,
      getUnitsOfLease,
      getLeaseFilesAndLease,
      getLease,
      getLeaseFromUnitLease,
      createLease,
      updateLease,
      deleteLease,
      createLeaseContact,
      updateLeaseContact,
      deleteLeaseContact,
      createAdditionalClause,
      getLeaseAdditionalClauses,
      updateAdditionalClause,
      deleteAdditionalClause,
      createUnitLease,
      deleteUnitLease,
      getAllContactsOwnersFromLease,
      deepDeleteLease,
      leasesError: undefined,
      updateUnitLease,
      createMandateLink,
      changeProfessionalActivityForLease,
      leasesLoading: loading,
      setFetchLease,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [leases, loading]
  );

  return <LeaseContext.Provider value={values}>{children}</LeaseContext.Provider>;
};

export const useLeases = (): LeaseContext => {
  const context = useContext<LeaseContext | null>(LeaseContext);

  if (context === undefined) {
    throw new Error('`useLeases` hook must be used within a `LeaseContextProvider` component');
  }
  return context as LeaseContext;
};
